import { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Accordion, Button, Modal } from 'react-bootstrap';
import { useAuth } from 'hooks';
import { BodyPart, BodyPartRepairDecision, RepairDecision } from 'redux/annotations/annotations.type';
import { useRedux, useReduxDispatch, useReduxSelector } from 'redux/store.hooks';

const REPAIR_DECISION_LABELS: { [key in RepairDecision]: string } = {
  [RepairDecision.Repair]: 'Repair',
  [RepairDecision.Replace]: 'Replace',
  [RepairDecision.Uncertain]: 'Uncertain',
  [RepairDecision.NoActionRequired]: 'No action required',
};

const CurrentDecision = styled.p`
  margin-bottom: 1rem;

  & .value {
    font-weight: 600;
  }
`;

const Buttons = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const DecisionButton = styled(Button)`
  min-height: 2.4rem;
  padding: 0rem 1rem;
`;

interface RepairDecisionReviewProps {
  photoSeriesId: string | undefined,
  repairDecision: BodyPartRepairDecision,
}

const RepairDecisionReview = (props: RepairDecisionReviewProps) => {
  const { photoSeriesId, repairDecision } = props;

  const [isSaving, setIsSaving] = useState(false);
  const [currentDecision, setCurrentDecision] = useState<RepairDecision | null>(repairDecision.repairDecision);
  const [pendingDecision, setPendingDecision] = useState<RepairDecision | null>(null);

  const { annotations: { actions: annotationsActions, selectors: annotationsSelectors } } = useRedux();
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();

  const { email } = useAuth();
  const photoSeries = useReduxSelector((state) => annotationsSelectors.selectPhotoSeriesByPhotoSeriesId(state, photoSeriesId as string));
  const isAssignedToMe = photoSeries !== undefined && photoSeries.analysisAssignedToUser === email;

  const currentDecisionLabel = currentDecision !== null
    ? REPAIR_DECISION_LABELS[currentDecision]
    : 'Not set';

  const handleDecision = (decision: RepairDecision) => {
    if (!photoSeriesId || !isAssignedToMe || isSaving) return;
    setPendingDecision(decision);
  };

  const handleConfirmDecision = () => {
    if (!photoSeriesId || isSaving || pendingDecision === null) return;

    setIsSaving(true);
    dispatch(annotationsActions.updateRepairDecision({
      photoSeriesId,
      bodyPart: BodyPart.Windshield,
      repairDecision: pendingDecision,
    })).then((data: any) => {
      setIsSaving(false);
      if (!data.meta.error) {
        setCurrentDecision(pendingDecision);
        setPendingDecision(null);
        navigate('/windshieldReviews');
      }
    });
  };

  return (
    <Accordion.Item eventKey="repair-decision-review">
      <Accordion.Header>Repair decision review</Accordion.Header>
      <Accordion.Body>
        <CurrentDecision>
          Current repair decision: <span className="value">{currentDecisionLabel}</span>
        </CurrentDecision>
        <Buttons>
          <DecisionButton
            variant="outline-danger"
            onClick={() => handleDecision(RepairDecision.Replace)}
            disabled={!isAssignedToMe || isSaving}
            data-testid="repairDecisionReplaceBtn"
          >
            Replace
          </DecisionButton>
          <DecisionButton
            variant="outline-success"
            onClick={() => handleDecision(RepairDecision.Repair)}
            disabled={!isAssignedToMe || isSaving}
            data-testid="repairDecisionRepairBtn"
          >
            Repair
          </DecisionButton>
          <DecisionButton
            variant="outline-primary"
            onClick={() => handleDecision(RepairDecision.NoActionRequired)}
            disabled={!isAssignedToMe || isSaving}
            data-testid="repairDecisionNoActionBtn"
          >
            No action required
          </DecisionButton>
        </Buttons>
        {pendingDecision !== null && (
          <Modal
            show
            onHide={() => setPendingDecision(null)}
            backdrop="static"
            keyboard={false}
          >
            <Modal.Header closeButton>
              <Modal.Title>Confirm repair decision</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              Are you sure &apos;{REPAIR_DECISION_LABELS[pendingDecision]}&apos; is the correct decision for the current windshield?
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="primary"
                onClick={handleConfirmDecision}
                disabled={isSaving}
                data-testid="repairDecisionConfirmYesBtn"
              >
                Yes
              </Button>
              <Button
                variant="secondary"
                onClick={() => setPendingDecision(null)}
                disabled={isSaving}
                data-testid="repairDecisionConfirmNoBtn"
              >
                No
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </Accordion.Body>
    </Accordion.Item>
  );
};

export default RepairDecisionReview;
